import { z } from 'zod';

import type { GiftPassBundle, GiftPassBundleDiscountType } from './gift-pass-bundle-contract.js';
import { isGiftPassTagIcon } from './gift-pass-tag-text.js';
import {
  GIFT_PASS_BUNDLE_MAX_TAGS,
  GIFT_PASS_TAG_COLORS,
  GIFT_PASS_TAG_TEXT_MAX_LENGTH,
} from './gift-pass.js';
import { formatCampaignDollars, TicketSalesCampaignDiscountType } from './ticket-sales-campaign.js';

/** A pass's base price where no workshop is close enough, before any running campaign. */
export const GIFT_PASS_FALLBACK_PRICE_CENTS = 8500;

export const GIFT_PASS_BUNDLE_NAME_MAX_LENGTH = 80;
/** One pass is always for sale on its own, so a bundle has at least two. */
export const GIFT_PASS_BUNDLE_MIN_PASSES = 2;
export const GIFT_PASS_BUNDLE_MAX_PASSES = 100;
export const GIFT_PASS_BUNDLE_PERCENT_MIN = 1;
export const GIFT_PASS_BUNDLE_PERCENT_MAX = 99;

/** The Postgres integer ceiling — a flat amount larger than this cannot be stored. */
const MAX_INT_CENTS = 2_147_483_647;

/** Any bracketed run counts as an icon code, so a typo is caught rather than shown as text. */
const BRACKETED = /\[([^[\]]*)\]/g;

export function unknownGiftPassTagIcons(text: string): string[] {
  return [...text.matchAll(BRACKETED)].map((m) => m[1]).filter((code) => !isGiftPassTagIcon(code));
}

/** What the undiscounted bundle costs at the fallback price: the ceiling on a flat discount. */
export function giftPassBundleFallbackListCents(passCount: number): number {
  return passCount * GIFT_PASS_FALLBACK_PRICE_CENTS;
}

export interface GiftPassBundleRuleInput {
  passCount: number;
  discountType: GiftPassBundleDiscountType;
  discountValue: number;
}

export function giftPassBundleRuleProblems(
  values: Partial<GiftPassBundleRuleInput>,
): Record<string, string> {
  const problems: Record<string, string> = {};
  const { passCount, discountType, discountValue } = values;
  if (discountValue === undefined) return problems;
  if (
    discountType === TicketSalesCampaignDiscountType.PERCENT &&
    (discountValue < GIFT_PASS_BUNDLE_PERCENT_MIN || discountValue > GIFT_PASS_BUNDLE_PERCENT_MAX)
  ) {
    problems.discountValue = `Enter a percentage from ${GIFT_PASS_BUNDLE_PERCENT_MIN} to ${GIFT_PASS_BUNDLE_PERCENT_MAX}.`;
  }
  if (
    discountType === TicketSalesCampaignDiscountType.FLAT &&
    passCount !== undefined &&
    discountValue >= giftPassBundleFallbackListCents(passCount)
  ) {
    const ceiling = formatCampaignDollars(giftPassBundleFallbackListCents(passCount));
    const pass = formatCampaignDollars(GIFT_PASS_FALLBACK_PRICE_CENTS);
    problems.discountValue = `A flat discount must be less than ${ceiling}, what ${passCount} passes cost at ${pass}.`;
  }
  return problems;
}

export const giftPassBundleTagInputSchema = z
  .object({
    text: z
      .string()
      .trim()
      .min(1, 'Give the tag some text, or remove it.')
      .max(
        GIFT_PASS_TAG_TEXT_MAX_LENGTH,
        `Keep the tag to ${GIFT_PASS_TAG_TEXT_MAX_LENGTH} characters, icon codes included.`,
      ),
    color: z.enum(GIFT_PASS_TAG_COLORS, { message: 'Pick one of the tag colors.' }),
  })
  .strict()
  .superRefine((tag, ctx) => {
    const [unknown] = unknownGiftPassTagIcons(tag.text);
    if (unknown === undefined) return;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['text'],
      message: `[${unknown}] isn’t an icon. Pick one from the list below.`,
    });
  });

const fields = {
  name: z
    .string()
    .trim()
    .min(1, 'Give the bundle a name.')
    .max(
      GIFT_PASS_BUNDLE_NAME_MAX_LENGTH,
      `Keep the name to ${GIFT_PASS_BUNDLE_NAME_MAX_LENGTH} characters.`,
    ),
  passCount: z
    .number({ message: `Enter a whole number of ${GIFT_PASS_BUNDLE_MIN_PASSES} or more.` })
    .int(`Enter a whole number of ${GIFT_PASS_BUNDLE_MIN_PASSES} or more.`)
    .min(
      GIFT_PASS_BUNDLE_MIN_PASSES,
      `Enter a whole number of ${GIFT_PASS_BUNDLE_MIN_PASSES} or more. A single pass is always sold on its own.`,
    )
    .max(
      GIFT_PASS_BUNDLE_MAX_PASSES,
      `A bundle can have at most ${GIFT_PASS_BUNDLE_MAX_PASSES} passes.`,
    ),
  discountType: z.nativeEnum(TicketSalesCampaignDiscountType, {
    message: 'Choose a percentage or a flat amount.',
  }),
  discountValue: z
    .number({ message: 'Enter the discount.' })
    .int('Enter the discount in whole cents.')
    .positive('Enter a discount greater than 0.')
    .max(MAX_INT_CENTS, 'That discount is too large.'),
  tags: z
    .array(giftPassBundleTagInputSchema)
    .max(GIFT_PASS_BUNDLE_MAX_TAGS, `A bundle can have at most ${GIFT_PASS_BUNDLE_MAX_TAGS} tags.`),
  isActive: z.boolean(),
  isPromoted: z.boolean(),
};

function addRuleIssues(values: Partial<GiftPassBundleRuleInput>, ctx: z.RefinementCtx): void {
  for (const [path, message] of Object.entries(giftPassBundleRuleProblems(values))) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
  }
}

export const giftPassBundleCreateSchema = z
  .object({
    name: fields.name,
    passCount: fields.passCount,
    discountType: fields.discountType,
    discountValue: fields.discountValue,
    tags: fields.tags.default([]),
    isPromoted: fields.isPromoted.default(false),
  })
  .strict()
  .superRefine(addRuleIssues);

/**
 * A PATCH checks only the fields it carries; the service re-checks the merged
 * bundle. A new discount type must come with its amount, since the stored
 * number means something else under the other type (20% is not $0.20).
 * Switching a bundle off or on is a PATCH of `isActive`.
 */
export const giftPassBundlePatchSchema = z
  .object({
    name: fields.name.optional(),
    passCount: fields.passCount.optional(),
    discountType: fields.discountType.optional(),
    discountValue: fields.discountValue.optional(),
    tags: fields.tags.optional(),
    isActive: fields.isActive.optional(),
    isPromoted: fields.isPromoted.optional(),
  })
  .strict()
  .refine((patch) => Object.values(patch).some((v) => v !== undefined), {
    message: 'Nothing to update',
  })
  .superRefine((patch, ctx) => {
    if (patch.discountType !== undefined && patch.discountValue === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['discountValue'],
        message: 'Send the amount along with the discount type.',
      });
    }
    if (patch.isActive === false && patch.isPromoted === true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['isPromoted'],
        message: 'Only an active bundle can be promoted.',
      });
    }
    addRuleIssues(patch, ctx);
  });

export type GiftPassBundleCreateRequest = z.input<typeof giftPassBundleCreateSchema>;
export type GiftPassBundleCreateInput = z.output<typeof giftPassBundleCreateSchema>;
export type GiftPassBundlePatchRequest = z.input<typeof giftPassBundlePatchSchema>;
export type GiftPassBundlePatchInput = z.output<typeof giftPassBundlePatchSchema>;

export interface GiftPassBundleResponse {
  bundle: GiftPassBundle;
}

export const giftPassBundlePreviewQuerySchema = z
  .object({
    passCount: z.coerce.number().pipe(fields.passCount),
    discountType: fields.discountType,
    discountValue: z.coerce.number().pipe(fields.discountValue),
  })
  .superRefine(addRuleIssues);

export type GiftPassBundlePreviewQuery = z.output<typeof giftPassBundlePreviewQuerySchema>;

export const GiftPassPreviewPriceBasis = {
  FALLBACK: 'FALLBACK',
  FALLBACK_WITH_CAMPAIGN: 'FALLBACK_WITH_CAMPAIGN',
  WORKSHOPS: 'WORKSHOPS',
} as const;
export type GiftPassPreviewPriceBasis =
  (typeof GiftPassPreviewPriceBasis)[keyof typeof GiftPassPreviewPriceBasis];

export interface GiftPassBundlePreviewPrice {
  basis: GiftPassPreviewPriceBasis;
  basePriceCents: number;
  /** How many upcoming public workshops are priced at this, for WORKSHOPS; else 0. */
  workshopCount: number;
  listPriceCents: number;
  /** Null when the discount would take the bundle to $0 here: it is not offered at this price. */
  bundlePriceCents: number | null;
  savingCents: number | null;
  salePricePerPassCents: number | null;
}

export interface GiftPassBundlePreviewResponse {
  windowDays: number;
  campaign: { name: string; discountLabel: string } | null;
  prices: GiftPassBundlePreviewPrice[];
}
